import { FunctionComponent, useEffect, useRef } from 'react'
import { PaginationMeta } from '../../../types/PaginationMeta'
import Button from '../Button/Button'

interface IPagination {
	pagination: PaginationMeta
	onPageChange: (newPageNumber: number) => void
	isLoading?: boolean
}

const Pagination: FunctionComponent<IPagination> = ({ pagination, onPageChange, isLoading }) => {
	// Some API responses omit first_page (or use an inconsistent key), so the
	// shared component must fall back to a sensible default rather than letting
	// comparisons against undefined silently disable navigation.
	const firstPage = pagination.first_page ?? 1
	const lastPage = pagination.last_page ?? pagination.current_page

	const loop = new Array(lastPage).fill(' ')

	// The loading skeleton replaces the real buttons with different DOM nodes, so any
	// keyboard focus in the control is lost. Capture which pagination control (if any)
	// currently holds focus during render, before this render's DOM commit can swap in
	// or out the loading skeleton and steal focus, then restore it once the real
	// buttons remount.
	const lastFocusedIdRef = useRef<string | null>(null)
	const activeId = (document.activeElement as HTMLElement | null)?.id
	if (activeId && activeId.startsWith('pagination-')) {
		lastFocusedIdRef.current = activeId
	}

	useEffect(() => {
		if (!isLoading && lastFocusedIdRef.current) {
			// After any pagination interaction, move focus to the current page
			// button. It is always present and focusable, so it gives keyboard
			// and screen-reader users a consistent "you are here" landmark and
			// avoids losing focus when the previously activated control (e.g. a
			// boundary chevron) becomes disabled.
			document.getElementById(`pagination-page-${pagination.current_page}`)?.focus()
			lastFocusedIdRef.current = null
		}
	}, [isLoading, pagination.current_page])

	const currentStartRange = pagination.current_page * pagination.per_page - pagination.per_page + 1
	const currentEndRange =
		pagination.current_page * pagination.per_page >= pagination.total
			? pagination.total
			: pagination.current_page * pagination.per_page

	if (isLoading) {
		return (
			<div className='animate-pulse rounded-lg bg-charcoal-600 p-2 mt-5 flex justify-between items-center w-full'>
				<div className='rounded-full bg-charcoal-500 h-4 w-48 mx-3 my-2' />
			<div className='flex mx-3 gap-1.5'>
					<div className='rounded-lg bg-charcoal-500 h-6 w-6 mx-1 my-2' />
					<div className='rounded-lg bg-charcoal-500 h-6 w-6 mx-1 my-2' />
					<div className='rounded-lg bg-charcoal-500 h-6 w-6 mx-1 my-2' />
				</div>
			</div>
		)
	}

	return (
		<div className='rounded-lg bg-charcoal-600 p-2 mt-5 flex justify-between items-center w-full'>
			{pagination.total !== 0 ? (
				<p className='text-white px-4' role='status' aria-live='polite'>
					Showing {currentStartRange} to {currentEndRange} of {pagination.total} results
				</p>
			) : (
				<p className='text-white px-4' role='status' aria-live='polite'>No results found</p>
			)}
			<div className='flex gap-1.5'>
				<Button
					id='pagination-prev'
					type='Pagination'
					hasLeadingIcon
					icon='fa-solid fa-chevron-left'
					additionalClass='w-12 px-0'
					disabled={pagination.current_page === firstPage}
					accessibilityLabel='Previous page'
					onClick={() => {
						// Previous
						if (pagination.current_page - 1 >= firstPage) {
							onPageChange && onPageChange(pagination.current_page - 1)
						}
					}}
				/>

				{loop.map((page, index) => {
					let renderPage = false
					let renderElipsis = false

					// First page number (always render)
					if (index + 1 === pagination.first_page) {
						renderPage = true
					}

					// The elipsis before
					if (index + 1 === pagination.current_page - 3) {
						// Check if this is the only hidden item, if it is, lets render a number instead
						if (pagination.current_page - 4 === pagination.first_page) {
							renderPage = true
						} else {
							renderElipsis = true
						}
					}

					// The 2nd page before
					if (index + 1 === pagination.current_page - 2) {
						renderPage = true
					}

					// The page before
					if (index + 1 === pagination.current_page - 1) {
						renderPage = true
					}

					// The current page (always render)
					if (index + 1 === pagination.current_page) {
						renderPage = true
					}

					// The page after
					if (index + 1 === pagination.current_page + 1) {
						renderPage = true
					}

					// The 2nd page after
					if (index + 1 === pagination.current_page + 2) {
						renderPage = true
					}

					// The elipsis after
					if (index + 1 === pagination.current_page + 3) {
						// Check if this is the only hidden item, if it is, lets render a number instead
						if (pagination.current_page + 4 === pagination.last_page) {
							renderPage = true
						} else {
							renderElipsis = true
						}
					}

					// Last page - 2 (If we're on the first page, we can show an extra one towards the end as we have spare slots)
					if (pagination.current_page === 1 && index + 1 === pagination.last_page - 2) {
						renderPage = true
					}

					// Last page - 1 (If we're on the first two pages, we can show an extra page towards the end of the list as we have spare slots)
					if (
						(pagination.current_page === 1 || pagination.current_page === 2) &&
						index + 1 === pagination.last_page - 1
					) {
						renderPage = true
					}

					// Last page number
					if (index + 1 === pagination.last_page) {
						renderPage = true
					}

					// Renders a page number button and also prevents an elipsis render by order of execution
					if (renderPage) {
						return (
							<Button
								key={index}
								id={`pagination-page-${index + 1}`}
								type={pagination.current_page - 1 === index ? 'Primary' : 'Pagination'}
								text={(index + 1).toString()}
								onClick={() => {
									onPageChange && onPageChange(index + 1)
								}}
							/>
						)
					}

					// Renders an elipsis button
					if (renderElipsis) {
						return (
							<Button
								key={index}
								type='Pagination'
								text={'...'}
								onClick={() => {
									onPageChange && onPageChange(index + 1)
								}}
							/>
						)
					}

					return null
				})}

				<Button
					id='pagination-next'
					type='Pagination'
					hasLeadingIcon
					icon='fa-solid fa-chevron-right'
					additionalClass='w-12 px-0'
					disabled={pagination.current_page === lastPage}
					accessibilityLabel='Next page'
					onClick={() => {
						// Next
						if (pagination.current_page + 1 <= lastPage) {
							onPageChange && onPageChange(pagination.current_page + 1)
						}
					}}
				/>
			</div>
		</div>
	)
}

export default Pagination
